import { Link } from "react-router-dom";
import useReveal from "../../components/useReveal";
import { Ic, icons } from "../../components/Icons";

const team = [
  {
    name: "Sviatoslav Gladyshev",
    role: "Chief Executive Officer",
    initials: "SG",
    roleShort: "CEO",
    slug: "sviatoslav-gladyshev",
    gradient: "linear-gradient(135deg, #6e40c9 0%, #c940a0 100%)",
    photo: "https://firebasestorage.googleapis.com/v0/b/oyola-ai.firebasestorage.app/o/sviatoslav_gladyshev.jpg?alt=media&token=09dace96-e2fd-4964-98e1-283507b4b8f0",
  },
  {
    name: "Artemii Mavlitov",
    role: "Chief Technology Officer",
    initials: "AM",
    roleShort: "CTO",
    slug: "artemii-mavlitov",
    gradient: "linear-gradient(135deg, #1a7fe8 0%, #00c2b3 100%)",
    photo: "https://firebasestorage.googleapis.com/v0/b/oyola-ai.firebasestorage.app/o/artemii_mavlitov.jpg?alt=media&token=e7e2b868-0d65-447c-a27c-8fce152e93c1",
  },
];

const values = [
  {
    icon: icons.eye,
    title: "Radical transparency",
    desc: "We are clear about product status, development stage, and what remains to be done. Architecture details and performance measurements remain confidential.",
  },
  {
    icon: icons.scan,
    title: "Measure before you claim",
    desc: "Claims must follow evidence. We measure and validate before describing a capability as achieved.",
  },
  {
    icon: icons.arrow,
    title: "Hardware pace, not software pace",
    desc: "Prototyping, validation, and manufacturing take time. We plan around those milestones and do not treat a prototype as a shipped product.",
  },
  {
    icon: icons.layers,
    title: "Co-design",
    desc: "We develop hardware and software together, with the workload guiding decisions in both.",
  },
];

export default function AboutPage() {
  useReveal();
  return (
    <div style={{ background: "var(--bg-primary)", paddingTop: 128 }}>
      {/* Origin */}
      <section className="section section-dark" id="team">
        <div className="container-narrow">
          <div className="reveal eyebrow">Company</div>
          <h1 className="reveal headline" style={{marginBottom:24}}>Ateno is building the spatial intelligence layer for the physical world.</h1>
          <p className="reveal" style={{fontSize:19,color:"var(--text-secondary)",lineHeight:1.65,marginBottom:16}}>
            We started with a commercial problem: spatial design and spatial intelligence are usually split across disconnected tools. The user-facing software and the compute layer should work as one stack.
          </p>
          <p className="reveal" style={{fontSize:19,color:"var(--text-secondary)",lineHeight:1.65,marginBottom:16}}>
            Ateno Studio is the application layer. Ateno AX is a coprocessor for 3D Gaussian Splatting workloads. Currently an FPGA prototype. Dedicated silicon is the next step.
          </p>
          <p className="reveal" style={{fontSize:19,color:"var(--text-secondary)",lineHeight:1.65}}>
            Ateno Inc. is a registered corporation building the commercial infrastructure for spatial intelligence.
          </p>
        </div>
      </section>

      <div className="divider" />

      {/* Values */}
      <section className="section section-dark">
        <div className="container">
          <div style={{textAlign:"center",marginBottom:52}}>
            <div className="reveal eyebrow">What we believe</div>
            <h2 className="reveal headline">Principles that shape the company.</h2>
          </div>
          <div className="grid-2 about-values-grid">
            {values.map((v, i) => (
              <article key={v.title} className={`reveal reveal-d${i + 1} about-value-card card-dark`}>
                <div className="about-value-inner">
                  <div className="feat-icon-wrap" aria-hidden="true">
                    <Ic d={v.icon} size={20} color="rgba(167,139,250,.88)" />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <h3 className="about-value-title">{v.title}</h3>
                    <p className="about-value-desc">{v.desc}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* Team */}
      <section className="section section-dark">
        <div className="container">
          <div style={{ textAlign: "center", marginBottom: 52 }}>
            <div className="reveal eyebrow">Leadership</div>
            <h2 className="reveal headline">Meet the team.</h2>
          </div>
          <div className="about-team-grid">
            {team.map((member, i) => (
              <Link
                key={member.slug}
                to={`/team/${member.slug}`}
                className={`reveal reveal-d${i + 1} about-team-card card-dark`}
                aria-label={`${member.name}, ${member.role} — view profile`}
              >
                <div className="about-team-photo-wrap">
                  {member.photo ? (
                    <img className="about-team-photo" src={member.photo} alt="" loading="lazy" />
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        background: member.gradient,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 40,
                        fontWeight: 700,
                        color: "#fff",
                        letterSpacing: "-.01em",
                        userSelect: "none",
                      }}
                    >
                      {member.initials}
                    </div>
                  )}
                </div>
                <div className="about-team-body">
                  <h3 className="about-team-name">{member.name}</h3>
                  <div className="about-team-meta">
                    <span className="about-team-role">{member.roleShort}</span>
                    <span className="about-team-hint">
                      Profile
                      <Ic d={icons.arrow} size={13} color="currentColor" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* Careers teaser */}
      <section className="section section-dark" style={{textAlign:"center"}}>
        <div className="container-narrow">
          <div className="reveal eyebrow">Careers</div>
          <h2 className="reveal headline" style={{marginBottom:14}}>Build the spatial layer of the world.</h2>
          <p className="reveal subhead" style={{maxWidth:520,margin:"0 auto 28px"}}>
            We are assembling our first technical team across hardware, graphics and spatial AI.
          </p>
          <Link to="/careers" className="reveal btn btn-white btn-md">Explore careers</Link>
        </div>
      </section>

      <div className="divider" />

      {/* Contact strip */}
      <section className="section section-dark" style={{textAlign:"center"}}>
        <div className="container-narrow">
          <h2 className="reveal headline" style={{marginBottom:14}}>Get in touch.</h2>
          <p className="reveal subhead" style={{maxWidth:400,margin:"0 auto 28px"}}>Questions, partnerships, press enquiries — we're reachable.</p>
          <div className="reveal" style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap"}}>
            <a href="mailto:info@ateno.co" className="btn btn-white btn-md">info@ateno.co</a>
            <a href="tel:+13022481852" className="btn btn-ghost btn-md">+1 (302) 248-1852</a>
          </div>
        </div>
      </section>
    </div>
  );
}
